import React from 'react';
import {
  openSalesforceChat,
  type SalesforceMessagingState,
} from '../embedded-chat';
import { useSalesforceMessagingState } from './use-salesforce-chat-state.hook';

type UseSalesforceChatCtaReturn = {
  /**
   * Whether a custom Salesforce chat button should be visible.
   *
   * This accounts for:
   * - Messaging readiness
   * - Current Salesforce window state
   * - The provided `enabled` flag
   */
  isVisible: boolean;
  /**
   * Approximate unread inbound message count for the current conversation.
   * Derived from Salesforce events; see `SalesforceMessagingState`.
   */
  unreadMessageCount: SalesforceMessagingState['unreadCount'];
  /**
   * Programmatically open the Salesforce chat window.
   * Consumers should typically call this from their custom button's onClick.
   */
  openChat: () => void;
  /**
   * Ref for the custom chat button element, used for focus management.
   * When the Salesforce window is minimized or closed after being maximized,
   * this hook will focus the button for better accessibility.
   */
  salesforceChatButtonRef: React.RefObject<HTMLButtonElement>;
};

/**
 * React hook that drives a custom Salesforce chat launcher button.
 *
 * Subscribes to Salesforce Messaging state and returns everything needed
 * to render and control a custom chat CTA:
 * - Visibility (accounts for messaging readiness and window state)
 * - Unread message count for badge display
 * - A callback to open the chat window
 * - A ref for the button element, used to restore focus when the chat window closes
 *
 * IMPORTANT: This hook does **not** initialize Salesforce Messaging itself.
 * Call `useSalesforceChat` (or `useInitSalesforceMessaging`) separately at a
 * higher level.
 *
 * @param enabled - When `false`, the hook is disabled and `isVisible` will be
 *   `false`. Defaults to `true`. Typical usage: gate on authenticated/private routes.
 */
export const useSalesforceChatCta = (
  enabled = true,
): UseSalesforceChatCtaReturn => {
  const messagingState = useSalesforceMessagingState(enabled);
  const { unreadCount, windowState, isReady, isChatBlocked } = messagingState;

  const salesforceChatButtonRef = React.useRef<HTMLButtonElement>(null);
  const previousWindowStateRef =
    React.useRef<SalesforceMessagingState['windowState']>(windowState);

  // Focus management: when the Salesforce window transitions from maximized to
  // minimized/closed, move focus back to the custom chat button.
  React.useEffect(() => {
    if (
      salesforceChatButtonRef.current &&
      previousWindowStateRef.current === 'maximized' &&
      (windowState === 'minimized' || windowState === 'closed')
    ) {
      salesforceChatButtonRef.current.focus();
    }

    previousWindowStateRef.current = windowState;
  }, [windowState]);

  const isVisible = React.useMemo(
    () =>
      enabled &&
      isReady &&
      !isChatBlocked &&
      // Hide the CTA when the Salesforce window itself is already maximized.
      windowState !== 'maximized',
    [enabled, isReady, isChatBlocked, windowState],
  );

  return {
    isVisible,
    unreadMessageCount: unreadCount,
    openChat: openSalesforceChat,
    salesforceChatButtonRef,
  };
};
